import { HugeiconsIcon } from '@hugeicons/react';
import {
  Award01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Calendar01Icon,
  CheckmarkCircle02Icon,
  Mail01Icon,
  Mortarboard01Icon,
  PlusSignIcon,
  SecurityCheckIcon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api, apiError } from '@/lib/api';
import { StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import { OperationalPageHeader, OperationalStatCard, operationalCardClass } from '@/components/shared/OperationalUI';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

// Etiqueta en español para el estado de vía rápida de una herramienta.
const fastLaneLabel = (fast) => (fast ? 'Vía rápida disponible' : 'Requiere evaluación');

export default function AdoptionHub() {
  const [model, setModel] = useState(null);
  const [paths, setPaths] = useState(null);
  const [activePath, setActivePath] = useState(null); // ruta abierta en el visor
  const [moduleIndex, setModuleIndex] = useState(0);
  const [savingModule, setSavingModule] = useState(false);
  const [enrolling, setEnrolling] = useState(null); // id de sesión en curso
  const navigate = useNavigate();

  const load = () =>
    Promise.all([api.get('/operating-system'), api.get('/learning/progress')])
      .then(([operating, learning]) => {
        setModel(operating.data);
        setPaths(learning.data.paths);
      })
      .catch((error) => toast.error(apiError(error)));

  useEffect(() => {
    load();
  }, []);

  // Abre el visor de módulos de una ruta en el primer módulo no completado.
  const openPath = (path) => {
    const firstPending = path.module_list?.findIndex((m) => !m.completed);
    setActivePath(path);
    setModuleIndex(firstPending && firstPending > 0 ? firstPending : 0);
  };

  const currentModule = activePath?.module_list?.[moduleIndex] || null;

  // Marca el módulo actual como completado (o lo desmarca) y refresca.
  const toggleModule = async (completed) => {
    if (!currentModule) return;
    setSavingModule(true);
    try {
      const { data } = await api.post(`/learning/modules/${currentModule.id}/complete`, { completed });
      // Actualiza el estado local de la ruta abierta para reflejo inmediato.
      setActivePath((prev) => {
        if (!prev) return prev;
        const module_list = prev.module_list.map((m) => (m.id === currentModule.id ? { ...m, completed } : m));
        return {
          ...prev,
          module_list,
          completed_modules: data.completed_modules,
          certification_status: data.certification_status,
        };
      });
      if (completed && data.certification_status === 'Certificada') {
        toast.success('¡Ruta completada y certificada!');
      } else if (completed) {
        toast.success('Módulo completado');
      } else {
        toast.message('Módulo marcado como pendiente');
      }
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSavingModule(false);
    }
  };

  // Al cerrar el visor, refresca las rutas para actualizar barras de progreso.
  const closeViewer = () => {
    setActivePath(null);
    load();
  };

  // Inscribe / confirma / cancela la asistencia a una sesión.
  const setEnrollment = async (session, status) => {
    setEnrolling(session.id);
    try {
      await api.post(`/help-sessions/${session.id}/enroll`, { status });
      const msg =
        status === 'confirmado'
          ? 'Asistencia confirmada'
          : status === 'cancelado'
            ? 'Inscripción cancelada'
            : 'Te inscribiste en la sesión';
      toast.success(msg);
      load();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setEnrolling(null);
    }
  };

  if (!model || !paths) {
    return (
      <div className="mx-auto grid max-w-7xl gap-4 px-6 py-10 md:grid-cols-2">
        {[...Array(6)].map((_, index) => (
          <Skeleton key={index} className="h-60 rounded-2xl" />
        ))}
      </div>
    );
  }

  const sessions = model.help_sessions || [];
  const champions = model.champions || [];

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-10">
      <OperationalPageHeader
        title="Academia y comunidad VIA"
        subtitle="Aprende por rol, certifícate y encuentra acompañamiento para adoptar la IA de forma segura y productiva."
      />

      <StaggerContainer className="grid gap-4 md:grid-cols-3" stagger={0.04}>
        <StaggerItem>
          <OperationalStatCard
            icon={Mortarboard01Icon}
            label="Rutas por rol"
            value={paths.length}
            subtitle="Aprendizaje personalizado"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={UserGroupIcon}
            label="Champions disponibles"
            value={champions.length}
            subtitle="Acompañamiento cercano"
            tone="blue"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={Calendar01Icon}
            label="Sesiones de ayuda"
            value={sessions.length}
            subtitle="Espacios abiertos semanales"
            tone="alert"
          />
        </StaggerItem>
      </StaggerContainer>

      <Tabs defaultValue="paths" className="flex flex-col gap-6">
        <TabsList className="h-auto w-full justify-start overflow-x-auto rounded-xl bg-muted/50 p-1 sm:w-fit">
          <TabsTrigger className="shrink-0" value="paths">
            Mis rutas
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="tools">
            Herramientas autorizadas
          </TabsTrigger>
          <TabsTrigger className="shrink-0" value="community">
            Comunidad y ayuda
          </TabsTrigger>
        </TabsList>

        {/* ── MIS RUTAS ── */}
        <TabsContent value="paths">
          <StaggerContainer className="grid gap-5 md:grid-cols-2" stagger={0.04}>
            {paths.map((path) => {
              const completed = path.completed_modules || 0;
              const total = path.modules || (path.module_list?.length ?? 0);
              const percent = total ? Math.round((completed / total) * 100) : 0;
              const certified = total > 0 && completed >= total;
              return (
                <StaggerItem key={path.id}>
                  <Card className={operationalCardClass}>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                          <HugeiconsIcon icon={Mortarboard01Icon} className="size-5" aria-hidden="true" />
                        </div>
                        <Badge variant="outline">{path.level}</Badge>
                      </div>
                      <CardTitle className="pt-3">{path.title}</CardTitle>
                      <CardDescription>
                        {path.role} · {path.duration}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-5">
                      <p className="text-sm leading-relaxed text-muted-foreground">{path.outcome}</p>
                      <div>
                        <div className="mb-2 flex justify-between text-xs text-muted-foreground">
                          <span>
                            {completed} de {total} módulos
                          </span>
                          <span>{percent}%</span>
                        </div>
                        <Progress value={percent} />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(path.module_list || []).map((m) => (
                          <Badge key={m.id} variant={m.completed ? 'secondary' : 'outline'}>
                            {m.completed && (
                              <HugeiconsIcon icon={CheckmarkCircle02Icon} className="mr-1 size-3" aria-hidden="true" />
                            )}
                            {m.title}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                    <CardFooter className="justify-between">
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        {certified && (
                          <HugeiconsIcon icon={Award01Icon} className="size-3.5 text-primary" aria-hidden="true" />
                        )}
                        {path.certification_status || 'No iniciada'}
                      </span>
                      <Button onClick={() => openPath(path)} variant={certified ? 'outline' : 'default'}>
                        {certified ? 'Repasar ruta' : completed > 0 ? 'Continuar' : 'Comenzar ruta'}
                        <HugeiconsIcon icon={ArrowRight01Icon} data-icon="inline-end" />
                      </Button>
                    </CardFooter>
                  </Card>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </TabsContent>

        {/* ── HERRAMIENTAS AUTORIZADAS ── */}
        <TabsContent value="tools">
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {model.approved_tools.map((tool) => (
              <Card key={tool.id} className={operationalCardClass}>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <HugeiconsIcon icon={SecurityCheckIcon} className="size-5" aria-hidden="true" />
                    </div>
                    <Badge variant={tool.status === 'Aprobada' ? 'default' : 'outline'}>{tool.status}</Badge>
                  </div>
                  <CardTitle className="pt-3">{tool.name}</CardTitle>
                  <CardDescription>{tool.category}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-3 text-sm">
                  <div className="rounded-xl bg-muted/50 p-3">
                    <p className="text-xs font-semibold text-muted-foreground">Datos permitidos</p>
                    <p className="mt-1 leading-relaxed">{tool.data_allowed}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">Roles: {tool.roles.join(', ')}</p>
                  <p className="text-xs text-muted-foreground">Responsable: {tool.owner}</p>
                </CardContent>
                <CardFooter className="flex-col items-stretch gap-3">
                  <Badge variant={tool.fast_lane ? 'secondary' : 'outline'} className="w-fit">
                    {fastLaneLabel(tool.fast_lane)}
                  </Badge>
                  <Button variant="outline" size="sm" onClick={() => navigate('/demanda/nueva')}>
                    <HugeiconsIcon icon={PlusSignIcon} data-icon="inline-start" />
                    Registrar un caso de uso
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ── COMUNIDAD Y AYUDA ── */}
        <TabsContent value="community">
          <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
            <Card className={operationalCardClass}>
              <CardHeader>
                <CardTitle>Red de champions</CardTitle>
                <CardDescription>
                  Personas cercanas que ayudan a traducir necesidades en experimentos seguros.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2">
                {champions.length === 0 && (
                  <p className="text-sm text-muted-foreground">Aún no hay champions publicados.</p>
                )}
                {champions.map((champion) => (
                  <div
                    key={champion.id || champion.name}
                    className="flex flex-col gap-2 rounded-xl border border-border p-3">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary/10 font-semibold text-secondary">
                        {champion.name
                          .split(' ')
                          .map((p) => p[0])
                          .join('')
                          .slice(0, 2)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{champion.name}</p>
                        <p className="text-xs text-muted-foreground">{champion.area}</p>
                      </div>
                    </div>
                    <p className="text-xs text-primary">{champion.specialty}</p>
                    {champion.contact && (
                      <a
                        href={`mailto:${champion.contact}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary hover:underline">
                        <HugeiconsIcon icon={Mail01Icon} className="size-3.5" aria-hidden="true" />
                        Contactar
                      </a>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className={operationalCardClass}>
              <CardHeader>
                <CardTitle>Sesiones de ayuda</CardTitle>
                <CardDescription>Acompañamiento abierto, sin necesidad de preparar una presentación.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {sessions.length === 0 && (
                  <p className="text-sm text-muted-foreground">No hay sesiones programadas por ahora.</p>
                )}
                {sessions.map((slot) => {
                  const inscrito = slot.my_status === 'inscrito' || slot.my_status === 'confirmado';
                  const confirmado = slot.my_status === 'confirmado';
                  const busy = enrolling === slot.id;
                  return (
                    <div key={slot.id} className="rounded-xl border border-border p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-semibold">{slot.weekday}</p>
                        <Badge variant="outline">{slot.time_range}</Badge>
                      </div>
                      <p className="mt-2 text-sm">{slot.topic}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{slot.channel}</p>
                      {slot.enrolled_count > 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">{slot.enrolled_count} inscrito(s)</p>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {!inscrito && (
                          <Button size="sm" disabled={busy} onClick={() => setEnrollment(slot, 'inscrito')}>
                            Inscribirme
                          </Button>
                        )}
                        {inscrito && !confirmado && (
                          <Button size="sm" disabled={busy} onClick={() => setEnrollment(slot, 'confirmado')}>
                            <HugeiconsIcon icon={CheckmarkCircle02Icon} data-icon="inline-start" />
                            Confirmar asistencia
                          </Button>
                        )}
                        {confirmado && (
                          <Badge variant="secondary" className="gap-1">
                            <HugeiconsIcon icon={CheckmarkCircle02Icon} className="size-3" aria-hidden="true" />
                            Asistencia confirmada
                          </Badge>
                        )}
                        {inscrito && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => setEnrollment(slot, 'cancelado')}>
                            Cancelar
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── VISOR DE MÓDULOS ── */}
      <Dialog open={!!activePath} onOpenChange={(open) => (!open ? closeViewer() : null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden p-0">
          {activePath && currentModule && (
            <div className="flex max-h-[90vh] flex-col">
              <DialogHeader className="border-b border-border px-6 py-4">
                <p className="text-xs font-medium text-muted-foreground">
                  {activePath.title} · Módulo {moduleIndex + 1} de {activePath.module_list.length}
                </p>
                <DialogTitle className="text-lg">{currentModule.title}</DialogTitle>
                {currentModule.estimated_minutes ? (
                  <p className="text-xs text-muted-foreground">{currentModule.estimated_minutes} min de lectura</p>
                ) : null}
              </DialogHeader>

              <div className="flex-1 overflow-y-auto px-6 py-5">
                {currentModule.summary && (
                  <p className="mb-4 text-sm leading-relaxed text-muted-foreground">{currentModule.summary}</p>
                )}
                <div
                  className="prose prose-sm max-w-none dark:prose-invert
                  prose-headings:font-semibold prose-p:leading-relaxed
                  prose-a:text-secondary prose-strong:text-foreground
                  prose-li:text-muted-foreground prose-blockquote:border-primary/40">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{currentModule.content}</ReactMarkdown>
                </div>
                {currentModule.kb_slug && (
                  <Button
                    variant="link"
                    className="mt-2 h-auto p-0 text-secondary"
                    onClick={() => navigate(`/kb/article/${currentModule.kb_slug}`)}>
                    Ver artículo completo en el Knowledge Hub
                    <HugeiconsIcon icon={ArrowRight01Icon} data-icon="inline-end" />
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={moduleIndex === 0}
                  onClick={() => setModuleIndex((i) => Math.max(0, i - 1))}>
                  <HugeiconsIcon icon={ArrowLeft01Icon} data-icon="inline-start" />
                  Anterior
                </Button>

                <Button
                  variant={currentModule.completed ? 'outline' : 'default'}
                  size="sm"
                  disabled={savingModule}
                  onClick={() => toggleModule(!currentModule.completed)}>
                  {currentModule.completed ? (
                    <>
                      <HugeiconsIcon icon={CheckmarkCircle02Icon} data-icon="inline-start" />
                      Completado
                    </>
                  ) : (
                    'Marcar como completado'
                  )}
                </Button>

                <Button
                  size="sm"
                  disabled={moduleIndex >= activePath.module_list.length - 1}
                  onClick={() => setModuleIndex((i) => Math.min(activePath.module_list.length - 1, i + 1))}>
                  Siguiente
                  <HugeiconsIcon icon={ArrowRight01Icon} data-icon="inline-end" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
